<script setup async lang="ts">
import AButton from "@addeus/ui-kit/components/AButton.vue";
import AIcon from "@addeus/ui-kit/components/AIcon.vue";
import ALabel from "@addeus/ui-kit/components/ALabel.vue";
import { useBreakpoints } from "@addeus/ui-kit/stores/breakpoint";
import { useNotification } from "@addeus/ui-kit/stores/notification";
import { useHead } from "@vueuse/head";
import { useTranslate } from "addeus-common-library/stores/translate";
import { useUserSession } from "addeus-common-library/stores/userSession";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import addeusAlone from "/@src/assets/logo/svg/addeus_alone.svg";
import addeusLight from "/@src/assets/logo/svg/addeus_light.svg";

const breakpoints = useBreakpoints();
const isMobile = breakpoints.smaller("md");

const isLoading = ref(false);
const username = ref("");
const password = ref("");
const hasRemember = ref(true);
const notification = useNotification();
const userSession = useUserSession();
const router = useRouter();
const translateStore = useTranslate();
const { t } = useI18n();

if (userSession.isLoggedIn.value) {
    const redirect = router.currentRoute.value.query.redirect;
    if (typeof redirect === "string") {
        void router.push(redirect);
    } else {
        void router.push({
            name: "dashboard",
        });
    }
}

const handleLogin = async () => {
    if (!isLoading.value) {
        isLoading.value = true;

        try {
            await userSession.login(username.value, password.value, hasRemember.value);

            notification.dismissAll();

            const redirect = router.currentRoute.value.query.redirect;
            if (typeof redirect === "string") {
                await router.push(redirect);
            } else {
                await router.push({
                    name: "dashboard",
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            notification.error(t("auth.login.error"));
        }

        isLoading.value = false;
    }
};

useHead({
    title: "Auth Login - Addeus",
});

router.afterEach(() => {
    useHead({
        title: "Addeus",
    });
});
const usernamePlaceholder = translateStore.translate(".login.emailPlaceholder");
const passwordPlaceholder = translateStore.translate(".login.passwordPlaceholder");

const showPassword = ref(false);
</script>

<template>
    <TranslateNamespace path=".login">
        <div class="container-login auth-wrapper-inner columns is-gapless">
            <!-- Intro section -->
            <div v-if="!isMobile" class="column is-8">
                <div class="visual-container">
                    <img
                        class="is-sidebar-logo is-align-self-left"
                        :src="addeusLight"
                        alt="addeus logo" />
                    <h1><Translate>.intro</Translate></h1>
                    <LottieIntro />
                </div>
            </div>

            <!-- Form section -->
            <div class="column is-4 form-container">
                <div class="hero is-fullheight">
                    <div class="hero-body">
                        <div class="container">
                            <div class="columns">
                                <div class="column is-12">
                                    <div class="auth-content">
                                        <div
                                            class="auth-logo is-flex is-justify-content-center mb-4">
                                            <RouterLink :to="{ name: 'dashboard' }">
                                                <img
                                                    :src="addeusAlone"
                                                    width="80"
                                                    height="80"
                                                    alt="" />
                                            </RouterLink>
                                        </div>
                                        <h2><Translate>.welcome</Translate></h2>
                                        <p><Translate>.subtitle</Translate></p>
                                    </div>
                                    <div class="auth-form-wrapper">
                                        <!-- Login Form -->
                                        <form @submit.prevent="handleLogin">
                                            <div class="login-form">
                                                <!-- Username -->
                                                <VField>
                                                    <ALabel>
                                                        <Translate>.email</Translate>
                                                    </ALabel>
                                                    <VControl>
                                                        <!-- <AInput
                                                            v-model="username"
                                                            color="var(--a-violet)"
                                                            full
                                                            type="text"
                                                            autocomplete="username" /> -->
                                                        <VInput
                                                            v-model="username"
                                                            type="text"
                                                            :placeholder="
                                                                usernamePlaceholder
                                                            "
                                                            autocomplete="username" />
                                                    </VControl>
                                                </VField>

                                                <!-- Password -->
                                                <VField>
                                                    <ALabel>
                                                        <Translate>.password</Translate>
                                                    </ALabel>
                                                    <VControl>
                                                        <!-- <AInput
                                                            v-model="password"
                                                            color="var(--a-violet)"
                                                            full
                                                            type="password"
                                                            autocomplete="current-password" /> -->
                                                        <VInput
                                                            v-model="password"
                                                            :type="
                                                                showPassword
                                                                    ? 'text'
                                                                    : 'password'
                                                            "
                                                            :placeholder="
                                                                passwordPlaceholder
                                                            "
                                                            autocomplete="current-password" />
                                                        <AButton
                                                            class="password-toggle"
                                                            type="button"
                                                            @click="
                                                                showPassword =
                                                                    !showPassword
                                                            ">
                                                            <AIcon
                                                                :icon="
                                                                    showPassword
                                                                        ? 'visibility'
                                                                        : 'visibility_off'
                                                                "></AIcon>
                                                        </AButton>
                                                    </VControl>
                                                </VField>

                                                <!-- Switch -->
                                                <VControl
                                                    class="setting-item remember-container">
                                                    <label
                                                        for="remember-me"
                                                        class="form-switch is-primary">
                                                        <input
                                                            id="remember-me"
                                                            v-model="hasRemember"
                                                            type="checkbox"
                                                            class="is-switch" />
                                                        <i aria-hidden="true"></i>
                                                    </label>
                                                    <div class="setting-meta">
                                                        <label for="remember-me">
                                                            <span>
                                                                <Translate
                                                                    >.remember</Translate
                                                                >
                                                            </span>
                                                        </label>
                                                    </div>
                                                </VControl>

                                                <!-- Submit -->
                                                <VControl class="login">
                                                    <AButton
                                                        id="login-button"
                                                        :loading="isLoading"
                                                        color="primary"
                                                        type="submit"
                                                        full>
                                                        <Translate
                                                            path=".submit"></Translate
                                                    ></AButton>
                                                </VControl>
                                                <div
                                                    class="forgot-link has-text-centered">
                                                    <RouterLink
                                                        to="/auth/reset-from-email"
                                                        ><Translate>
                                                            .forgot
                                                        </Translate></RouterLink
                                                    >
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </TranslateNamespace>
</template>

<style scoped lang="scss">
.container-login {
    .is-sidebar-logo {
        width: 12rem;
        height: 3rem;
        margin-left: unset;
    }

    input {
        padding-right: 70px;
    }

    .password-toggle {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
    }

    .auth-content {
        text-align: center;
    }
}
</style>
